<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed, toRef } from 'vue';
import MockCanvas from '../MockCanvas.vue';
import FeatureCardTextEdit from './cards/FeatureCardTextEdit.vue';
import FeatureCardRemoveBg from './cards/FeatureCardRemoveBg.vue';
import FeatureCardUpscale from './cards/FeatureCardUpscale.vue';
import FeatureCardImageStyles from './cards/FeatureCardImageStyles.vue';
import FeatureCardDeliver from './cards/FeatureCardDeliver.vue';
import { useAuth } from '@/composables/useAuth';
import { useScaledEmbedResize } from '@/composables/useScaledEmbedResize';
import { Amplitude } from '@seede/amplitude';
import { i18n, t } from '@/i18n';

const props = withDefaults(
  defineProps<{
    /** Docs / learn pages: tighter layout with scaled demos */
    embedded?: boolean;
  }>(),
  { embedded: false }
);

const embeddedRef = toRef(props, 'embedded');

const mockEmbed = useScaledEmbedResize(embeddedRef, 720, 600, { minScale: 0.24, maxScale: 0.62 });
const textEditEmbed = useScaledEmbedResize(embeddedRef, 640, 480, {
  minScale: 0.24,
  maxScale: 0.62,
});
const deliverEmbed = useScaledEmbedResize(embeddedRef, 900, 380, {
  minScale: 0.24,
  maxScale: 0.62,
});

const { containerRef: mockWrapRef, innerStyle: mockInnerStyle } = mockEmbed;
const { containerRef: textEditWrapRef, innerStyle: textEditInnerStyle } = textEditEmbed;
const { containerRef: deliverWrapRef, innerStyle: deliverInnerStyle } = deliverEmbed;

const { isAuthenticated } = useAuth();
const pagePath = computed(() => (import.meta.client ? window.location.href : ''));

const trackDesignNow = (source: string) => {
  const target = isAuthenticated.value ? '/workspace/create' : '/signup';
  Amplitude.event('nav_link_click', {
    link_text: 'Design now',
    link_target: target,
    section: props.embedded ? 'learn_intro_every_step' : 'features_every_step',
    source,
    auth_state: isAuthenticated.value ? 'authenticated' : 'anonymous',
    page_path: pagePath.value,
  });
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const contentInPanel = ref<HTMLElement | null>(null);
const isContentInAnimating = ref(false);
const hasContentInAnimated = ref(false);
const contentInMockCanvasRef = ref<InstanceType<typeof MockCanvas> | null>(null);
let contentInDemoTimer: ReturnType<typeof setTimeout> | null = null;

const contentInDemoMessage = computed(() => {
  void i18n.locale;
  return t('landing_home.mock.user_message');
});

const getContentInMockCanvas = () => contentInMockCanvasRef.value;

const clearContentInDemoTimer = () => {
  if (contentInDemoTimer !== null) {
    clearTimeout(contentInDemoTimer);
    contentInDemoTimer = null;
  }
};

const playContentInDemo = async () => {
  if (isContentInAnimating.value) return;

  isContentInAnimating.value = true;
  try {
    await nextTick();

    let mockCanvas = getContentInMockCanvas();
    if (!mockCanvas) return;

    mockCanvas.resetDemo();
    await sleep(300);

    mockCanvas = getContentInMockCanvas();
    if (!mockCanvas) return;
    await mockCanvas.typeText(contentInDemoMessage.value, 20);
    await sleep(300);

    mockCanvas = getContentInMockCanvas();
    if (!mockCanvas) return;
    mockCanvas.sendDemoMessage();
    mockCanvas.startArtboardLoading();
    await sleep(500);

    mockCanvas = getContentInMockCanvas();
    if (!mockCanvas) return;
    await mockCanvas.showAiMessageAnimated();
    await sleep(300);

    mockCanvas = getContentInMockCanvas();
    if (!mockCanvas) return;
    mockCanvas.finishArtboardLoading();

    hasContentInAnimated.value = true;
  } finally {
    isContentInAnimating.value = false;
  }
};

let contentInObserver: IntersectionObserver | null = null;

onMounted(() => {
  contentInObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasContentInAnimated.value && !isContentInAnimating.value) {
          clearContentInDemoTimer();
          contentInDemoTimer = setTimeout(() => {
            contentInDemoTimer = null;
            void playContentInDemo();
          }, 500);
        }
      });
    },
    { threshold: props.embedded ? 0.25 : 0.5 }
  );

  if (contentInPanel.value) {
    contentInObserver.observe(contentInPanel.value);
  }
});

onUnmounted(() => {
  clearContentInDemoTimer();
  if (contentInObserver) {
    contentInObserver.disconnect();
  }
});
</script>

<template>
  <section
    id="features-every-step"
    :class="embedded ? 'min-w-0 overflow-visible py-0 px-0' : 'py-16 md:py-40 px-4'"
  >
    <div :class="embedded ? 'w-full min-w-0' : 'max-w-[1320px] mx-auto'">
      <h2
        v-if="!embedded"
        class="font-boldonse text-[clamp(1.5rem,4vw,2rem)] px-8 md:px-10 text-black uppercase leading-[1.6] mb-6 md:mb-12 landing-title-multiline landing-title-section-lg"
      >
        <span class="block landing-title-line">{{
          $t('landing_home.every_step.section_heading_line1')
        }}</span>
        <span class="block landing-title-line">{{
          $t('landing_home.every_step.section_heading_line2')
        }}</span>
      </h2>

      <div :class="embedded ? 'grid grid-cols-1 gap-4' : 'grid grid-cols-1 gap-6'">
        <!-- Content In Panel -->
        <div
          ref="contentInPanel"
          class="relative rounded-3xl bg-white bg-cover bg-center flex flex-col md:flex-row gap-4 p-2 md:p-4"
          :class="
            embedded
              ? 'overflow-visible rounded-2xl border border-black/5'
              : 'overflow-hidden min-h-[70vh] md:min-h-0 md:h-[80vh] md:max-h-[680px]'
          "
        >
          <div
            class="w-full md:w-[32%] flex flex-col shrink-0"
            :class="embedded ? 'p-4 md:p-5' : 'p-6'"
          >
            <span
              class="font-boldonse text-black uppercase"
              :class="
                embedded
                  ? 'text-[10px] sm:text-[11px] text-black/55 mb-6 md:mb-0'
                  : 'text-lg md:text-xl mb-12 md:mb-0 landing-panel-label'
              "
              >{{ $t('landing_home.every_step.panel01.label') }}</span
            >

            <div class="flex-1 flex items-center">
              <div :class="embedded ? 'space-y-1' : ''">
                <h3
                  class="font-normal text-black/90"
                  :class="embedded ? 'text-sm sm:text-[15px] leading-snug' : 'text-lg md:text-xl'"
                >
                  {{ $t('landing_home.every_step.panel01.title') }}
                </h3>
                <p
                  class="font-normal text-black/40"
                  :class="embedded ? 'text-xs sm:text-sm leading-relaxed' : 'text-base md:text-xl'"
                >
                  {{ $t('landing_home.every_step.panel01.body') }}
                </p>
              </div>
            </div>

            <div class="pt-4">
              <NuxtLink
                :to="isAuthenticated ? '/workspace/create' : '/signup'"
                class="rounded-full bg-black/90 text-white font-medium hover:bg-black transition-colors inline-block"
                :class="embedded ? 'px-3 py-1.5 text-xs' : 'px-5 py-2.5 text-sm'"
                @click="trackDesignNow('generate_panel')"
              >
                {{ $t('landing_home.every_step.panel01.cta') }}
              </NuxtLink>
            </div>
          </div>

          <div
            v-if="embedded"
            ref="mockWrapRef"
            class="flex w-full min-w-0 flex-1 flex-col items-center justify-center"
          >
            <div :style="mockInnerStyle">
              <MockCanvas ref="contentInMockCanvasRef" variant="light" class="h-full w-full" />
            </div>
          </div>
          <div v-else class="flex-1 flex items-center justify-center">
            <div class="w-full h-full min-h-[300px]">
              <MockCanvas
                ref="contentInMockCanvasRef"
                variant="light"
                initial-state="empty"
                :default-sidebar-open="true"
              />
            </div>
          </div>
        </div>

        <!-- Edit Panel -->
        <div
          id="design-out"
          class="relative rounded-3xl bg-white flex flex-col md:flex-row gap-8 p-2 md:p-4"
          :class="
            embedded
              ? 'overflow-visible rounded-2xl border border-black/5'
              : 'overflow-hidden min-h-[70vh] md:min-h-0 md:h-[72vh] md:max-h-[680px]'
          "
        >
          <div
            class="w-full md:w-[32%] flex flex-col shrink-0"
            :class="embedded ? 'p-4 md:p-5' : 'p-4 md:p-6'"
          >
            <span
              class="font-boldonse text-black uppercase"
              :class="
                embedded
                  ? 'text-[10px] sm:text-[11px] text-black/55 mb-6 md:mb-0'
                  : 'text-lg md:text-xl mb-12 md:mb-0 landing-panel-label'
              "
              >{{ $t('landing_home.every_step.panel02.label') }}</span
            >

            <div class="flex-1 flex items-center">
              <div :class="embedded ? 'space-y-1' : ''">
                <h3
                  class="font-normal text-black/90"
                  :class="embedded ? 'text-sm sm:text-[15px] leading-snug' : 'text-lg md:text-xl'"
                >
                  {{ $t('landing_home.every_step.panel02.title') }}
                </h3>
                <p
                  class="font-normal text-black/40"
                  :class="embedded ? 'text-xs sm:text-sm leading-relaxed' : 'text-lg md:text-xl'"
                >
                  {{ $t('landing_home.every_step.panel02.body') }}
                </p>
              </div>
            </div>

            <div class="pt-4">
              <NuxtLink
                :to="isAuthenticated ? '/workspace/create' : '/signup'"
                class="rounded-full bg-black/90 text-white font-medium hover:bg-black transition-colors inline-block"
                :class="embedded ? 'px-3 py-1.5 text-xs' : 'px-5 py-2.5 text-sm'"
                @click="trackDesignNow('edit_panel')"
              >
                {{ $t('landing_home.every_step.panel02.cta') }}
              </NuxtLink>
            </div>
          </div>

          <div
            v-if="embedded"
            ref="textEditWrapRef"
            class="flex w-full min-w-0 flex-1 flex-col items-center justify-center"
          >
            <div :style="textEditInnerStyle">
              <FeatureCardTextEdit hide-label class="h-full w-full min-h-0" />
            </div>
          </div>
          <div v-else class="flex-1 min-h-[300px] flex items-center justify-center overflow-hidden">
            <FeatureCardTextEdit class="w-full h-full" hide-label />
          </div>
        </div>

        <!-- Deliver Panel -->
        <div
          class="relative rounded-3xl bg-white flex flex-col md:flex-row gap-8 p-2 md:p-4"
          :class="
            embedded
              ? 'overflow-visible rounded-2xl border border-black/5'
              : 'overflow-hidden min-h-[70vh] md:min-h-0 md:h-[80vh] md:max-h-[680px]'
          "
        >
          <div
            class="w-full md:w-[32%] flex flex-col shrink-0"
            :class="embedded ? 'p-4 md:p-5' : 'p-4 md:p-6'"
          >
            <span
              class="font-boldonse text-black uppercase"
              :class="
                embedded
                  ? 'text-[10px] sm:text-[11px] text-black/55 mb-6 md:mb-0'
                  : 'text-lg md:text-xl mb-12 md:mb-0 landing-panel-label'
              "
              >{{ $t('landing_home.every_step.panel03.label') }}</span
            >

            <div class="flex-1 flex items-center">
              <div :class="embedded ? 'space-y-1' : ''">
                <h3
                  class="font-normal text-black/90"
                  :class="embedded ? 'text-sm sm:text-[15px] leading-snug' : 'text-lg md:text-xl'"
                >
                  {{ $t('landing_home.every_step.panel03.title') }}
                </h3>
                <p
                  class="font-normal text-black/40"
                  :class="embedded ? 'text-xs sm:text-sm leading-relaxed' : 'text-lg md:text-xl'"
                >
                  {{ $t('landing_home.every_step.panel03.body') }}
                </p>
              </div>
            </div>

            <div class="pt-4">
              <NuxtLink
                :to="isAuthenticated ? '/workspace/create' : '/signup'"
                class="rounded-full bg-black/90 text-white font-medium hover:bg-black transition-colors inline-block"
                :class="embedded ? 'px-3 py-1.5 text-xs' : 'px-5 py-2.5 text-sm'"
                @click="trackDesignNow('deliver_panel')"
              >
                {{ $t('landing_home.every_step.panel03.cta') }}
              </NuxtLink>
            </div>
          </div>

          <div
            v-if="embedded"
            ref="deliverWrapRef"
            class="flex w-full min-w-0 flex-1 flex-col items-center justify-center"
          >
            <div :style="deliverInnerStyle">
              <FeatureCardDeliver class="h-full w-full" hide-label />
            </div>
          </div>
          <div v-else class="flex-1 min-h-[300px] flex items-center justify-center overflow-hidden">
            <FeatureCardDeliver class="w-full h-full" hide-label />
          </div>
        </div>
      </div>

      <template v-if="!embedded">
        <h2
          class="font-boldonse text-[clamp(1rem,4vw,1.5rem)] px-4 md:px-8 text-black uppercase leading-[1.6] mb-6 md:mb-12 mt-16 md:mt-24 text-center landing-title-section-sm"
        >
          {{ $t('landing_home.every_step.more_explore_heading') }}
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <FeatureCardRemoveBg />
          <FeatureCardUpscale />
          <FeatureCardImageStyles />
        </div>
      </template>
    </div>
  </section>
</template>
